import { computed, inject, Injectable } from '@angular/core';
import { DashboardProfileContextService } from '@rma/dashboard/authentication/public/user';
import { SearchModalComponent } from '@rma/dashboard/generic/orc/search';
import { RmaModalConfig, RmaModalService } from '@rma/dashboard/generic/ui/modal';
import { Environment } from '@rma/dashboard/generic/util/environment';
import { isIndividual } from '@rma/generic/domain/helpers';
import { combineLatest, take } from 'rxjs';
import { NotificationModalData, NotificationModalResult } from '../domain';
import { NotificationService } from '../domain/services/notification.service';
import { NotificationModalComponent } from '../ui-notification-modal/notification-modal.component';

@Injectable()
export class HeaderFacade {
  private readonly environment = inject(Environment);
  private readonly modalService = inject(RmaModalService);
  private readonly notificationService = inject(NotificationService);
  private readonly profileCtx = inject(DashboardProfileContextService);

  public readonly helpRoute = computed(() => this.environment.trainingUrl ?? null);

  constructor() {
    combineLatest([
      this.profileCtx.shortName$.pipe(take(1)),
      this.profileCtx.profileType$.pipe(take(1)),
      this.notificationService.getNextNotification().pipe(take(1)),
    ]).subscribe(([shortName, profileType, notification]) => {
      if (!notification) {
        return;
      }

      const modalData: NotificationModalData = {
        ...notification,
        shortName,
        userType: isIndividual(profileType) ? 'Agent' : 'Agency',
      };
      const modalConfig: RmaModalConfig<NotificationModalData> = {
        data: modalData,
        width: '38rem',
      };

      const modalRef = modalData.displayConfetti
        ? this.modalService.openCelebrationFullScreenMobile<NotificationModalComponent, NotificationModalData>(
            NotificationModalComponent,
            modalConfig,
          )
        : this.modalService.openFullScreenOnMobileOnly<NotificationModalComponent, NotificationModalData>(
            NotificationModalComponent,
            modalConfig,
          );

      modalRef.afterClosed().subscribe((result: NotificationModalResult | null) => {
        this.notificationService.dismissUserNotification(notification.userNotificationId).pipe(take(1)).subscribe();

        if (result === 'actioned') {
          this.notificationService.actionUserNotification(notification.userNotificationId).pipe(take(1)).subscribe();
        }
      });
    });
  }

  public openSearchModal(): void {
    this.modalService.openFullScreenOnMobileOnly(SearchModalComponent);
  }
}
